import React, { useState, useEffect } from 'react';
import Footer from '../Header/Footer';
import Header from '../Header/Header';
import Config from '../Header/Config';
import { ToastContainer, toast } from 'react-toastify';

export default function Dailybook() {
  const today = new Date().toISOString().split('T')[0];
  const [date, setDate] = useState(today);
  const [name, setName] = useState('');
  const [credit, setCredit] = useState('');
  const [debit, setDebit] = useState('');
  const [paymentMode, setPaymentMode] = useState('');
  const [note, setNote] = useState('');
  const [isDataChanged, setIsDataChanged] = useState(false);

  // Validation states
  const [errors, setErrors] = useState({
    name: "",
    date: "",
    credit: "",
    debit: "",
    creditOrDebit: "",
    paymentMode: "",
    note: "",
  });
  const [touched, setTouched] = useState({
    name: false,
    date: false,
    credit: false,
    debit: false,
    paymentMode: false,
    note: false,
  });
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const apiUrl = Config.apiUrl;

  const capitalizeWords = (str) => {
    if (!str) return '';
    return str
      .toString()
      .split(/\s+/)
      .map((w) => (w.length > 0 ? w[0].toUpperCase() + w.slice(1).toLowerCase() : ''))
      .join(' ');
  };

  // Validation functions
  const validateName = (value) => {
    if (!value || value.trim() === "") {
      return "Name of entry is required";
    }
    if (value.trim().length < 2) {
      return "Name must be at least 2 characters";
    }
    if (value.trim().length > 100) {
      return "Name must not exceed 100 characters";
    }
    return "";
  };

  const validateDate = (value) => {
    if (!value || value.trim() === "") {
      return "Date is required";
    }
    const selectedDate = new Date(value);
    selectedDate.setHours(0, 0, 0, 0);
    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);
    // Allow today's date and past dates, but not future dates
    if (selectedDate > todayDate) {
      return "Date cannot be in the future";
    }
    return "";
  };

  const validateCredit = (value, debitValue) => {
    // If debit is filled, credit should be empty (mutually exclusive)
    if (debitValue && debitValue.trim() !== '') {
      if (value && value.trim() !== '') {
        return "Cannot enter both Credit and Debit. Please fill only one.";
      }
      return "";
    }
    // If credit is filled, validate it's a valid number
    if (value && value.trim() !== '') {
      const cleanValue = value.replace(/[,\s]/g, '');
      if (!/^\d+(\.\d{1,2})?$/.test(cleanValue)) {
        return "Credit amount must be a valid number";
      }
      const numValue = parseFloat(cleanValue);
      if (numValue <= 0) {
        return "Credit amount must be greater than 0";
      }
      if (numValue > 999999999) {
        return "Credit amount is too large";
      }
    }
    return "";
  };

  const validateDebit = (value, creditValue) => {
    // If credit is filled, debit should be empty (mutually exclusive)
    if (creditValue && creditValue.trim() !== '') {
      if (value && value.trim() !== '') {
        return "Cannot enter both Credit and Debit. Please fill only one.";
      }
      return "";
    }
    // If debit is filled, validate it's a valid number
    if (value && value.trim() !== '') {
      const cleanValue = value.replace(/[,\s]/g, '');
      if (!/^\d+(\.\d{1,2})?$/.test(cleanValue)) {
        return "Debit amount must be a valid number";
      }
      const numValue = parseFloat(cleanValue);
      if (numValue <= 0) {
        return "Debit amount must be greater than 0";
      }
      if (numValue > 999999999) {
        return "Debit amount is too large";
      }
    }
    return "";
  };

  const validateCreditOrDebit = (creditValue, debitValue) => {
    // At least one must be filled
    const hasCredit = creditValue && creditValue.trim() !== '';
    const hasDebit = debitValue && debitValue.trim() !== '';
    
    if (!hasCredit && !hasDebit) {
      return "Either Credit or Debit amount is required";
    }
    return "";
  };

  const validatePaymentMode = (value) => {
    if (!value || value.trim() === "") {
      return "Payment mode is required";
    }
    return "";
  };

  const validateNote = (value) => {
    if (value && value.trim().length > 0) {
      if (value.trim().length < 2) {
        return "Note must be at least 2 characters if provided";
      }
      if (value.trim().length > 200) {
        return "Note must not exceed 200 characters";
      }
    }
    return "";
  };

  // Validate all fields
  const validateAll = () => {
    const nameError = validateName(name);
    const dateError = validateDate(date);
    const creditError = validateCredit(credit, debit);
    const debitError = validateDebit(debit, credit);
    const creditOrDebitError = validateCreditOrDebit(credit, debit);
    const paymentModeError = validatePaymentMode(paymentMode);
    const noteError = validateNote(note);

    const newErrors = {
      name: nameError,
      date: dateError,
      credit: creditError,
      debit: debitError,
      creditOrDebit: creditOrDebitError,
      paymentMode: paymentModeError,
      note: noteError,
    };
    setErrors(newErrors);
    return !newErrors.name && !newErrors.date && !newErrors.credit && !newErrors.debit && !newErrors.creditOrDebit && !newErrors.paymentMode && !newErrors.note;
  };

  // useEffect for handleBeforeUnload
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (isDataChanged) {
        const message = 'You have unsaved changes. Are you sure you want to leave?';
        event.returnValue = message;
        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDataChanged]);

  // handleHeaderNavigation function
  const handleHeaderNavigation = () => {
    if (isDataChanged && !window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
      return false;
    }
    return true;
  };

  const AddDailyBook = async (e) => {
    e.preventDefault();

    // Mark submit as attempted
    setSubmitAttempted(true);

    // Mark all fields as touched
    setTouched({
      name: true,
      date: true,
      credit: true,
      debit: true,
      paymentMode: true,
      note: true,
    });

    // Validate all fields
    if (!validateAll()) {
      return;
    }

    // Clean amounts - remove commas and spaces
    const cleanCredit = credit ? credit.replace(/[,\s]/g, '') : '';
    const cleanDebit = debit ? debit.replace(/[,\s]/g, '') : '';

    let item = {
      entry_name: name.trim(),
      date: date,
      creadit_amount: cleanCredit,
      debit_amount: cleanDebit,
      payment_mode: paymentMode,
      note: note.trim(),
    };

    try {
      let result = await fetch(`${apiUrl}/createEntry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(item),
      });

      if (result.ok) {
        toast.success('Dailybook added successfully', { autoClose: 1500 });
        setName('');
        setDebit('');
        setCredit('');
        setNote('');
        setPaymentMode('');
        setDate(today);
        setErrors({
          name: "",
          date: "",
          credit: "",
          debit: "",
          creditOrDebit: "",
          paymentMode: "",
          note: "",
        });
        setTouched({
          name: false,
          date: false,
          credit: false,
          debit: false,
          paymentMode: false,
          note: false,
        });
        setSubmitAttempted(false);
        setIsDataChanged(false);
        
        // Reload the page after successful submission
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        toast.error('Failed to add Dailybook', { autoClose: 1500 });
      }
    } catch (error) {
      console.log(error);
      toast.error('Failed to add Dailybook', { autoClose: 1500 });
    }
  };

  // cancelFields function
  const cancelDailyBook = () => {
    if (isDataChanged && !window.confirm('You have unsaved changes. Are you sure you want to cancel?')) {
      return;
    }

    setName('');
    setDebit('');
    setCredit('');
    setNote('');
    setPaymentMode('');
    setDate(today);
    setErrors({
      name: "",
      date: "",
      credit: "",
      debit: "",
      creditOrDebit: "",
      paymentMode: "",
      note: "",
    });
    setTouched({
      name: false,
      date: false,
      credit: false,
      debit: false,
      paymentMode: false,
      note: false,
    });
    setSubmitAttempted(false);
    setIsDataChanged(false);
  };

  const handleNameChange = (e) => {
    const value = capitalizeWords(e.target.value);
    setName(value);
    setIsDataChanged(true);
    
    // Validate on change if field has been touched or submit was attempted
    if (touched.name || submitAttempted) {
      setErrors((prev) => ({
        ...prev,
        name: validateName(value),
      }));
    }
  };

  const handleNameBlur = () => {
    setTouched((prev) => ({ ...prev, name: true }));
    setErrors((prev) => ({
      ...prev,
      name: validateName(name),
    }));
  };

  const handleDateChange = (e) => {
    const value = e.target.value;
    setDate(value);
    setIsDataChanged(true);
    
    // Validate on change if field has been touched or submit was attempted
    if (touched.date || submitAttempted) {
      setErrors((prev) => ({
        ...prev,
        date: validateDate(value),
      }));
    }
  };

  const handleDateBlur = () => {
    setTouched((prev) => ({ ...prev, date: true }));
    setErrors((prev) => ({
      ...prev,
      date: validateDate(date),
    }));
  };

  const handleCreditChange = (e) => {
    let value = e.target.value;
    // Allow only digits, dots, commas, and spaces
    value = value.replace(/[^\d.,\s]/g, '');
    setCredit(value);
    
    // Maintain mutual exclusivity - clear debit if credit is filled
    if (value && value.trim() !== '') {
      setDebit('');
      // Clear debit errors when credit is filled
      setErrors((prev) => ({
        ...prev,
        debit: "",
        creditOrDebit: "",
      }));
    }
    
    setIsDataChanged(true);
    
    // Validate on change if field has been touched or submit was attempted
    if (touched.credit || submitAttempted) {
      setErrors((prev) => ({
        ...prev,
        credit: validateCredit(value, debit),
        creditOrDebit: validateCreditOrDebit(value, debit),
      }));
    }
  };

  const handleCreditBlur = () => {
    setTouched((prev) => ({ ...prev, credit: true }));
    setErrors((prev) => ({
      ...prev,
      credit: validateCredit(credit, debit),
      creditOrDebit: validateCreditOrDebit(credit, debit),
    }));
  };

  const handleDebitChange = (e) => {
    let value = e.target.value;
    // Allow only digits, dots, commas, and spaces
    value = value.replace(/[^\d.,\s]/g, '');
    setDebit(value);
    
    // Maintain mutual exclusivity - clear credit if debit is filled
    if (value && value.trim() !== '') {
      setCredit('');
      // Clear credit errors when debit is filled
      setErrors((prev) => ({
        ...prev,
        credit: "",
        creditOrDebit: "",
      }));
    }
    
    setIsDataChanged(true);
    
    // Validate on change if field has been touched or submit was attempted
    if (touched.debit || submitAttempted) {
      setErrors((prev) => ({
        ...prev,
        debit: validateDebit(value, credit),
        creditOrDebit: validateCreditOrDebit(credit, value),
      }));
    }
  };

  const handleDebitBlur = () => {
    setTouched((prev) => ({ ...prev, debit: true }));
    setErrors((prev) => ({
      ...prev,
      debit: validateDebit(debit, credit),
      creditOrDebit: validateCreditOrDebit(credit, debit),
    }));
  };

  const handlePaymentModeChange = (e) => {
    const value = e.target.value;
    setPaymentMode(value);
    setIsDataChanged(true);
    
    // Validate on change if field has been touched or submit was attempted
    if (touched.paymentMode || submitAttempted) {
      setErrors((prev) => ({
        ...prev,
        paymentMode: validatePaymentMode(value),
      }));
    }
  };

  const handlePaymentModeBlur = () => {
    setTouched((prev) => ({ ...prev, paymentMode: true }));
    setErrors((prev) => ({
      ...prev,
      paymentMode: validatePaymentMode(paymentMode),
    }));
  };

  const handleNoteChange = (e) => {
    const value = capitalizeWords(e.target.value);
    setNote(value);
    setIsDataChanged(true);
    
    // Validate on change if field has been touched or submit was attempted
    if (touched.note || submitAttempted) {
      setErrors((prev) => ({
        ...prev,
        note: validateNote(value),
      }));
    }
  };

  const handleNoteBlur = () => {
    setTouched((prev) => ({ ...prev, note: true }));
    setErrors((prev) => ({
      ...prev,
      note: validateNote(note),
    }));
  };

  return (
    <>
      <ToastContainer />
      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header onNavigate={handleHeaderNavigation} />
          <div className="layout-page">
            <div className="content-wrapper">
              <div className="container-xxl flex-grow-1 container-p-y">
                <h4 className="fw-bold py-3 mb-4">Daily Book Details</h4>
                <div className="card mb-4">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="mb-0 heading-heighlight">Add New Entry</h5>
                  </div>
                  <div className="card-body">
                    <form onSubmit={AddDailyBook} noValidate>
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label className="form-label" htmlFor="basic-default-fullname">
                            Name Of Entry <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className={`form-control ${(touched.name && errors.name) || (submitAttempted && !name.trim()) ? 'is-invalid' : touched.name && !errors.name && name ? 'is-valid' : ''}`}
                            id="basic-default-fullname"
                            placeholder="Enter Name"
                            value={name}
                            onChange={handleNameChange}
                            onBlur={handleNameBlur}
                          />
                          {((touched.name && errors.name) || (submitAttempted && !name.trim())) && (
                            <div className="invalid-feedback d-block">
                              {errors.name || "Name of entry is required"}
                            </div>
                          )}
                          {touched.name && !errors.name && name && (
                            <div className="valid-feedback d-block">
                              Looks good!
                            </div>
                          )}
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label" htmlFor="basic-default-date">
                            Date <span className="text-danger">*</span>
                          </label>
                          <input
                            type="date"
                            className={`form-control ${(touched.date && errors.date) || (submitAttempted && !date.trim()) ? 'is-invalid' : touched.date && !errors.date && date ? 'is-valid' : ''}`}
                            id="basic-default-date"
                            placeholder="Enter date"
                            value={date}
                            onChange={handleDateChange}
                            onBlur={handleDateBlur}
                            max={today}
                          />
                          {((touched.date && errors.date) || (submitAttempted && !date.trim())) && (
                            <div className="invalid-feedback d-block">
                              {errors.date || "Date is required"}
                            </div>
                          )}
                          {touched.date && !errors.date && date && (
                            <div className="valid-feedback d-block">
                              Looks good!
                            </div>
                          )}
                        </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label" htmlFor="basic-default-credit">
                          Credit Amount <span className="text-danger">*</span>
                        </label>
                        <div className="input-group input-group-merge">
                          <input
                            type="text"
                            id="basic-default-credit"
                            className={`form-control ${(touched.credit && errors.credit) || (submitAttempted && errors.credit) ? 'is-invalid' : touched.credit && !errors.credit && credit ? 'is-valid' : ''}`}
                            placeholder="Enter Amount"
                            aria-label="Credit Amount"
                            aria-describedby="credit-error"
                            value={credit}
                            onChange={handleCreditChange}
                            onBlur={handleCreditBlur}
                            disabled={debit && debit.trim() !== ''}
                          />
                        </div>
                        {(touched.credit && errors.credit) || (submitAttempted && errors.credit) ? (
                          <div className="invalid-feedback d-block" id="credit-error">
                            {errors.credit}
                          </div>
                        ) : null}
                        {touched.credit && !errors.credit && credit && (
                          <div className="valid-feedback d-block">
                            Looks good!
                          </div>
                        )}
                        {errors.creditOrDebit && (
                          <div className="invalid-feedback d-block">
                            {errors.creditOrDebit}
                          </div>
                        )}
                        <small className="text-muted">
                          Enter amount (e.g., 1000 or 1000.50)
                        </small>
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label" htmlFor="basic-default-debit">
                          Debit Amount <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          id="basic-default-debit"
                          className={`form-control phone-mask ${(touched.debit && errors.debit) || (submitAttempted && errors.debit) ? 'is-invalid' : touched.debit && !errors.debit && debit ? 'is-valid' : ''}`}
                          placeholder="Enter Amount"
                          value={debit}
                          onChange={handleDebitChange}
                          onBlur={handleDebitBlur}
                          disabled={credit && credit.trim() !== ''}
                        />
                        {(touched.debit && errors.debit) || (submitAttempted && errors.debit) ? (
                          <div className="invalid-feedback d-block">
                            {errors.debit}
                          </div>
                        ) : null}
                        {touched.debit && !errors.debit && debit && (
                          <div className="valid-feedback d-block">
                            Looks good!
                          </div>
                        )}
                        <small className="text-muted">
                          Enter amount (e.g., 1000 or 1000.50)
                        </small>
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label" htmlFor="basic-default-payment-mode">
                          Payment Mode <span className="text-danger">*</span>
                        </label>
                        <select
                          className={`form-select ${(touched.paymentMode && errors.paymentMode) || (submitAttempted && !paymentMode.trim()) ? 'is-invalid' : touched.paymentMode && !errors.paymentMode && paymentMode ? 'is-valid' : ''}`}
                          id="basic-default-payment-mode"
                          value={paymentMode}
                          onChange={handlePaymentModeChange}
                          onBlur={handlePaymentModeBlur}
                        >
                          <option value="" disabled>
                            Select Payment Mode
                          </option>
                          <option value="Net Banking">Net Banking</option>
                          <option value="Cash Deposit">Cash Deposit</option>
                          <option value="Paytm">Paytm</option>
                          <option value="Phone Pay">Phone Pay</option>
                          <option value="Google Pay">Google Pay</option>
                        </select>
                        {((touched.paymentMode && errors.paymentMode) || (submitAttempted && !paymentMode.trim())) && (
                          <div className="invalid-feedback d-block">
                            {errors.paymentMode || "Payment mode is required"}
                          </div>
                        )}
                        {touched.paymentMode && !errors.paymentMode && paymentMode && (
                          <div className="valid-feedback d-block">
                            Looks good!
                          </div>
                        )}
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label" htmlFor="basic-default-note">
                          Note <small className="text-muted">(Optional)</small>
                        </label>
                        <input
                          type="text"
                          id="basic-default-note"
                          className={`form-control ${(touched.note && errors.note) ? 'is-invalid' : touched.note && !errors.note && note ? 'is-valid' : ''}`}
                          placeholder="Enter Note"
                          value={note}
                          onChange={handleNoteChange}
                          onBlur={handleNoteBlur}
                          maxLength={200}
                        />
                        {touched.note && errors.note && (
                          <div className="invalid-feedback d-block">
                            {errors.note}
                          </div>
                        )}
                        {touched.note && !errors.note && note && (
                          <div className="valid-feedback d-block">
                            Looks good!
                          </div>
                        )}
                        <small className="text-muted">
                          {note.length}/200 characters
                        </small>
                      </div>

                      <div className="col-12 d-flex justify-content-end">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          style={{ marginLeft: '5px', marginRight: '5px' }}
                        >
                          Submit
                        </button>
                        <button type="button" className="btn btn-danger" onClick={cancelDailyBook}>
                          Clear
                        </button>
                      </div>
                    </div>
                    </form>
                  </div>
                </div>
              </div>
              <Footer />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
